import React, { useEffect, useState } from 'react';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import { ShieldOff, Download, AlertTriangle, Smartphone, Radio } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (secs) => {
  if (!secs || secs <= 0) return '0m 00s';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}m ${String(s).padStart(2, '0')}s`;
};

const OpSec = () => {
  const [status, setStatus] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [isAndroid, setIsAndroid] = useState(true);

  useEffect(() => {
    setIsAndroid(/android/i.test(navigator.userAgent));
  }, []);

  // Poll status every 20s so a fresh admin flip is picked up without a page
  // reload. The local timer ticks every second between polls.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/opsec/status`, { cache: 'no-store' });
        const data = await r.json();
        if (!cancelled) {
          setStatus(data);
          setSeconds(data.seconds_remaining || 0);
        }
      } catch { /* ignore transient */ }
    };
    load();
    const poll = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(poll); };
  }, []);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const t = setInterval(() => setSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [seconds > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const enabled = status?.enabled && seconds > 0 && !!status?.apk_url;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="opsec-page">
      <TopNav />
      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 mb-3">
          <Radio className="h-4 w-4 text-red-500" />
          <p className="text-red-500 text-[11px] font-black uppercase tracking-[0.35em]">Op Sec</p>
        </div>
        <h1
          className="text-4xl sm:text-5xl font-black leading-tight mb-4"
          style={{ fontFamily: 'Georgia, serif' }}
          data-testid="opsec-headline"
        >
          S0PHIA Ops
        </h1>
        <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mb-4">
          A field-grade signals tool for anyone who wants to see what&apos;s in the air around them.
          Runs on Android. No root. No permissions you don&apos;t already give your phone.
        </p>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8">
          S0PHIA Ops surfaces simple signals in Victoria Police and other Australian LE-agency equipment
          that were never considered in the manufacturing process. It&apos;s the same app the ABC called a
          &ldquo;secret app&rdquo;. We&apos;re making it publicly available because of the way our government
          restricts us and our movements.
        </p>

        {/* Warning strip - Android only, non-root, offered as-is */}
        <div className="mb-6 rounded border border-yellow-700/50 bg-yellow-950/30 px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-yellow-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-300 text-[10px] font-black uppercase tracking-[0.25em] mb-1">Read this first</p>
            <ul className="text-zinc-300 text-sm leading-relaxed list-disc list-inside space-y-1">
              <li>Android only. iOS is not supported.</li>
              <li>No root required. Do not sideload if you don&apos;t know what &ldquo;sideload&rdquo; means.</li>
              <li>Enable <em className="text-white">Install unknown apps</em> for your browser, install, then turn it back off.</li>
              <li>Offered as-is. Use at your own discretion. Nothing on this page is legal advice.</li>
            </ul>
          </div>
        </div>

        {/* Download card */}
        <div
          data-testid="opsec-download-card"
          className={`rounded-lg border-2 overflow-hidden mb-8 ${
            enabled
              ? 'border-emerald-500/70 bg-gradient-to-br from-emerald-950/40 to-black shadow-[0_0_28px_rgba(16,185,129,0.25)]'
              : 'border-zinc-800 bg-zinc-950/60'
          }`}
        >
          <div className="px-5 py-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className={`text-[10px] font-black uppercase tracking-[0.3em] mb-1 flex items-center gap-2 ${enabled ? 'text-emerald-300' : 'text-zinc-500'}`}>
                  <span aria-hidden className={`inline-block h-2 w-2 rounded-full ${enabled ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-600'}`} />
                  {enabled ? 'Download window open' : 'Downloads currently closed'}
                </p>
                <p className="text-white text-lg font-black" style={{ fontFamily: 'Georgia, serif' }}>
                  {status?.apk_filename || 'S0PHIA-Ops.apk'}
                </p>
                <p className="text-zinc-400 text-xs mt-1">
                  {status?.apk_version && <span className="mr-3">Version {status.apk_version}</span>}
                  {status?.apk_size_mb && <span>{status.apk_size_mb} MB</span>}
                </p>
                {status?.sha256 && (
                  <p className="text-zinc-500 text-[10px] mt-2 break-all" data-testid="opsec-sha">
                    SHA-256: <span className="font-mono">{status.sha256}</span>
                  </p>
                )}
              </div>
              <Smartphone className={`h-8 w-8 shrink-0 ${enabled ? 'text-emerald-400' : 'text-zinc-600'}`} />
            </div>

            {enabled ? (
              <>
                <a
                  href={status.apk_url}
                  download={status.apk_filename || 'S0PHIA-Ops.apk'}
                  rel="noopener noreferrer"
                  data-testid="opsec-download-btn"
                  className={`mt-5 inline-flex items-center gap-2 h-12 px-6 rounded font-black text-sm uppercase tracking-[0.2em] transition-colors ${
                    isAndroid
                      ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                      : 'bg-zinc-800 hover:bg-zinc-700 text-white'
                  }`}
                >
                  <Download className="h-4 w-4" />
                  {isAndroid ? 'Download S0PHIA Ops' : 'Download (Android device required)'}
                </a>
                <div
                  className="mt-4 inline-flex items-baseline gap-2 rounded bg-black/60 border border-emerald-700/50 px-3 py-1.5"
                  aria-live="polite"
                  data-testid="opsec-countdown"
                >
                  <span className="text-[9px] font-black uppercase tracking-[0.3em] text-emerald-500/80">
                    Window closes in
                  </span>
                  <span
                    className="text-emerald-300 font-black text-base tabular-nums leading-none"
                    style={{ fontFamily: 'Georgia, serif' }}
                  >
                    {fmt(seconds)}
                  </span>
                </div>
                {!isAndroid && (
                  <p className="text-zinc-400 text-xs mt-3">
                    You&apos;re not on Android - the APK will still download but won&apos;t install on iOS or desktop.
                  </p>
                )}
              </>
            ) : (
              <div className="mt-5 flex items-start gap-3 rounded border border-zinc-800 bg-black/40 px-4 py-3">
                <ShieldOff className="h-4 w-4 text-zinc-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-black text-sm mb-1">Downloads are closed right now.</p>
                  <p className="text-zinc-400 text-xs leading-relaxed">
                    We open a short window from time to time to keep hosting costs and mirroring risk low.
                    Check back soon or sign up to get a note the next time it opens.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="text-zinc-500 text-[11px] leading-relaxed">
          <p className="mb-2">
            <strong className="text-zinc-300">Why:</strong> the tools built to watch us leak more than the people
            behind them realise. S0PHIA Ops lets you see the same things they can. That is not a crime - it is
            a fact of how the equipment was made.
          </p>
          <p>
            <strong className="text-zinc-300">Disclaimer:</strong> Outlaw Media does not develop S0PHIA Ops.
            We host a mirror when the window is open. What you do with what you learn is on you.
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default OpSec;
